import { UseFormReturn } from 'react-hook-form';
import { IForms } from '@models/Forms/IForms';
import { IFieldElem, IFormData } from '@models/IFormData';
import { IDocumentData } from '@models/document/IDocumentData';
import { IRequiredValidation } from '@models/actions/IRequiredValidation';
import { IMailRemindersBlockModel } from '@models/mailRemindersBlock/IMailRemindersBlockModel';
import { IMailRemindersOptions } from '@molecules/AddMailReminderBlock/AddMailReminderBlock';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { RemindersService } from '@services/RemindersService';
import { checkAttachValidation } from '@utils/documentUtils';
import { ValidatorManager } from '@utils/managers/ValidatorManager';
import { getUploadingFileLabel, IUploadProgressState, UploadProgressHandler } from '@utils/uploadProgress';

export interface IBatchEditingSubmitCheckParams {
    files?: ISelectedFiles;
    attachFiles?: boolean;
    attachRequired?: boolean;
    attachRequiredValidation?: IRequiredValidation[];
    form?: IForms;
    fieldsData?: Record<string, IFieldElem>;
    methods: UseFormReturn<IDocumentData>;
}

export interface IBatchEditingSubmitCheck {
    /** Текст предупреждения модалки; сохранять нельзя. */
    warning?: string;
    /** Ошибки валидаторов схемы уже показаны сообщением; сохранять нельзя. */
    invalid?: boolean;
}

/** Проверки перед сохранением: обязательные вложения и валидаторы схемы формы. */
export async function checkBatchEditingSubmit({
    files,
    attachFiles,
    attachRequired,
    attachRequiredValidation,
    form,
    fieldsData,
    methods,
}: IBatchEditingSubmitCheckParams): Promise<IBatchEditingSubmitCheck> {
    // проверка на обязательность наличия аттачей
    if (attachFiles && attachRequired && (files?.files === undefined || files?.files.length === 0)) {
        return { warning: 'Необходимо приложить файл' };
    }

    if (attachRequiredValidation && attachRequiredValidation.length > 0) {
        const checkResult = checkAttachValidation(attachRequiredValidation, files);
        if (checkResult.length > 0) {
            return { warning: checkResult.join('\n') };
        }
    }

    const errors: string[] = [];

    if (form?.view?.validators && fieldsData) {
        const validatorManager = new ValidatorManager(fieldsData, methods);
        await validatorManager.validateAllAsync(form?.view.validators?.validator, errors);

        if (errors.length > 0) {
            sendModalMsg({
                message: errors,
                type: 'error',
            });
            return { invalid: true };
        }
    }

    return {};
}

/** Почтовые напоминания по сохранённым полям — создаются после успешного сохранения. */
export async function createBatchEditingMailReminders(
    docId: string,
    actionKey: string,
    val: IFormData,
    model: IMailRemindersBlockModel,
    options: IMailRemindersOptions,
): Promise<void> {
    const remIds = val.fields.find((item) => item.name === model.reminderUserIdsField)?.value ?? '';
    const remTargetDate = new Date(
        val.fields.find((item) => item.name === model.reminderTargetDateField)?.value as string,
    );
    const remText = val.fields.find((item) => item.name === model.reminderTextField)?.value ?? '';

    const dayOfWeek =
        options.periodical !== undefined && options.periodical.dayOfWeek !== undefined
            ? options.periodical.dayOfWeek[0]?.value
            : '';

    await RemindersService.createBlockMailReminders(
        docId,
        actionKey,
        remText?.toString(),
        remTargetDate.toISOString(),
        remIds?.toString(),
        options.daysBefore,
        options.periodical.weekly === 1 ? 'true' : 'false',
        options.periodical.monthly === 1 ? 'true' : 'false',
        dayOfWeek,
    );
}

/** Прогресс отправки файлов: процент и подпись на кнопке сохранения. */
export const batchEditingUploadProgress =
    (setProgress: (value?: number) => void, setLabel: (value?: string) => void): UploadProgressHandler =>
    (state: IUploadProgressState) => {
        setProgress(state.progress);
        setLabel(state.progress >= 100 ? 'Сохраняю изменения...' : getUploadingFileLabel(state));
    };
